import type { CurriculumCefrLevel } from '@speaking-coach/shared-types/src/cefr.js'
import type { ProgramSummary, ProgramLevelSummary } from '../common/programs'
/**
 * DTOs for the admin student endpoints. Mirrors the server CamelDto.
 * The roster is GLOBAL (ADR-0052 D1 revised): rows carry their client
 * client, and the create call targets one via the route path.
 */
import type { CohortRow } from '../cohorts/types'
import type { EmailUndeliverable } from '../email_suppressions/types'

export type ProductTier = 'essential' | 'pro' | 'premium' | 'unlimited'

export const LIFECYCLE_STAGES = [
  'provisioned',
  'invite_sent',
  'activated',
  'onboarding',
  'placed',
  'learning',
  'suspended',
  'access_ended',
] as const
export type LifecycleStage = (typeof LIFECYCLE_STAGES)[number]

export const ATTENTION_REASONS = [
  'invitation_not_sent',
  'invite_send_failed',
  'invite_send_overdue',
  'invite_expiring',
  'invite_expired',
  'activation_overdue',
  'onboarding_overdue',
  'first_lesson_overdue',
  'placement_review',
  'rubric_review',
  'suspended_early',
  'continuity_delayed',
] as const
export type AttentionReason = (typeof ATTENTION_REASONS)[number]

/**
 * Where a student is in the invite lifecycle. DERIVED server-side from the
 * activation tokens plus `activatedAt` — never stored — so the roster, the
 * detail page and the later cohort funnel cannot disagree.
 */
export type InviteState =
  | 'none'
  /** A send is planned for a future date; nothing minted yet. */
  | 'scheduled'
  /**
   * A planned send is long past and nothing was minted — i.e. the hourly
   * sweep is not running. Distinct from `scheduled` because there is nothing
   * to wait for, and distinct from `none` because it did not happen for a
   * reason.
   */
  | 'overdue'
  /** An operator canceled the auto-send. Reversible; no token was touched. */
  | 'suppressed'
  | 'pending'
  /**
   * The send itself failed, so the student has nothing — even though a live
   * token exists. Derived from the token alone this would read `pending`,
   * i.e. "waiting for them to set a password" about someone who received no
   * link to act on.
   */
  | 'failed'
  | 'expired'
  | 'activated'

export interface InviteActionResult {
  userId: string
  inviteState: InviteState
  inviteExpiresAt: string | null
}

/** One student in the global admin roster / the result of an edit. */
export interface StudentRow {
  /** Retained calls; populated by detail responses, not the roster. */
  callHistoryCount?: number | null
  clientEmployeeId?: string | null
  clientDepartment?: string | null
  userId: string
  /** The client the student belongs to. */
  clientId: string
  clientName: string
  email: string
  name: string
  firstName?: string | null
  lastName?: string | null
  username?: string | null
  addressSuffix?: 'san' | 'none' | null
  identityConfirmedAt?: string | null
  identityVersion?: number
  tier: ProductTier
  onboarded: boolean
  enrollmentStatus:
    | 'draft'
    | 'pending'
    | 'active'
    | 'suspended'
    | 'ended'
    | 'cancelled'
    | null
  /** The cohort attached to the focused enrollment, or null. */
  cohortId: string | null
  cohortName: string | null
  /**
   * Data classification: this student's activity is excluded from
   * customer analytics. Never an access flag. Shown on the roster because
   * the failure runs both ways and is silent otherwise — a tester counted
   * as a customer, or a customer dropped from the numbers.
   */
  isInternal: boolean
  /**
   * The two values `isInternal` was resolved FROM at creation (`cohort ??
   * client`). Both are the levels as they stand NOW — the classification is
   * copy-on-create, so a later cohort edit leaves the student's stamped
   * value behind. When they disagree with `isInternal`
   * that is not a bug: it is the only available explanation for "why is this
   * student internal when their cohort says customer?".
   *
   * `cohortIsInternal` is THREE-STATE. null means no cohort, or a cohort that
   * expresses no opinion and falls through to the client. An explicit false
   * is a customer cohort inside an internal client and beats the client
   * default — which is why the resolver is `??` and not `or`.
   */
  clientIsInternal: boolean
  cohortIsInternal: boolean | null
  /** ISO timestamp — client creation (users.created_at). */
  createdAt: string
  /**
   * ISO timestamp of the student's most recent PRACTICE CALL start, or
   * null if they have never started one. Not a last-sign-in time: the
   * schema has no login timestamp.
   */
  lastActiveAt: string | null
  /** Immutable start date snapshotted on the focused enrollment. */
  scheduledStartOn: string | null
  resolvedScheduledStartOn: string | null
  cohortScheduledStartOn: string | null
  /** Immutable end date snapshotted on the focused enrollment. */
  accessEndsOn: string | null
  programAccessEndsOn: string | null
  cohortAccessEndsOn: string | null
  /** Explicit curriculum-state key + its ISO enrollment anchor. */
  initialProgramLevelId: string | null
  curriculumStartedAt: string | null
  /**
   * The invite half of the lifecycle. `expired` is the operator's cue: the
   * link was sent, never used, and nothing live remains — distinct from
   * `pending`, where waiting is still the right action.
   */
  inviteState: InviteState
  /**
   * WHY the send failed — the server's short stable code
   * (`rejected_hard-bounce`, `timeout`, `template_misconfigured`,
   * `window_closed`, …). Non-null ONLY while `inviteState` is `failed`;
   * a recovered row carries null even though the server never clears the
   * underlying stamp. Older rows may be `failed` with null (stamped
   * before the reason column existed).
   */
  inviteSendFailedReason: string | null
  /** When the outstanding (or most recent) invite was sent. */
  inviteSentAt: string | null
  /**
   * How many invitation links have EVER been minted — each mint kills the
   * previous, so N sends mean N-1 dead links in their inbox. Detail
   * responses only; absent/null on the roster.
   */
  inviteSendCount?: number | null
  inviteExpiresAt: string | null
  /**
   * Active deliverability suppression for the student's address
   * (bounce/complaint webhook). Detail responses only; absent on the
   * roster. Distinct from `inviteState === 'suppressed'`, which means
   * an operator cancelled a scheduled send.
   */
  emailUndeliverable?: EmailUndeliverable | null
  /**
   * The resolved send time. Set in `scheduled` and `overdue` — never in
   * `suppressed`, which must not advertise a send that will not happen.
   */
  inviteScheduledFor: string | null
  /**
   * The raw per-enrollment override, as opposed to the resolved
   * `inviteScheduledFor` above. Both are shown for the same reason the
   * start-date pair is: an operator has to tell an inherited cohort schedule
   * from a deliberate exception on this one student.
   */
  inviteSendAtOverride: string | null
  /** When the auto-send was canceled, or null. Drives the un-cancel control. */
  inviteSuppressedAt: string | null
  /** When the student redeemed an invite and chose their own password. */
  activatedAt: string | null
  lifecycleStage: LifecycleStage
  needsAttention: boolean
  attentionReasons: AttentionReason[]
}

export interface ListStudentsParams {
  /** Narrow to one client. */
  clientId?: string
  /**
   * Narrow to one cohort. Sufficient on its own — a cohort id already
   * implies its client — but the two compose.
   */
  cohortId?: string
  /**
   * One side of the analytics carve-out. `undefined` means don't filter,
   * which is NOT the same as `false` — omitting it returns both.
   */
  isInternal?: boolean
  /**
   * `failed` narrows to students whose last send failed and was never
   * superseded — the reselect half of bulk resend (PROV-ALERTS). The ONLY
   * supported value: the other derived states resolve cohort inheritance
   * server-side in Python, so the API refuses them (422) rather than
   * answering a silently-empty list.
   */
  inviteState?: 'failed'
  /**
   * One enrollment lifecycle value, a plain column predicate server-side — so
   * the filter stays a bounded SQL query however large the roster grows.
   * `archived` is written by the PROV-END access-end sweep (resolved
   * end + grace) — the terminal state.
   */
  enrollmentStatus?:
    | 'draft'
    | 'pending'
    | 'active'
    | 'suspended'
    | 'ended'
    | 'cancelled'
  /** Inclusive focused-enrollment start-date boundaries (Japan business dates). */
  startsOnOrAfter?: string
  startsOnOrBefore?: string
  lifecycleStage?: LifecycleStage
  needsAttention?: boolean
  attentionReason?: AttentionReason
  /** Server-side ilike over email + name. */
  search?: string
  /** Page size, server max 200. */
  limit?: number
  offset?: number
}

export interface StudentListResponse {
  students: StudentRow[]
  /** Matches of the current filters, independent of limit/offset. */
  total: number
}

export interface BulkResendInvitesBody {
  userIds: string[]
}

/** `queued` is durable scheduler ownership; the others are per-row refusals. */
export type BulkResendOutcome = 'queued' | 'not_eligible' | 'rate_limited'

export interface BulkResendRowResult {
  userId: string
  outcome: BulkResendOutcome
  /** Eligibility reason or send-failure reason; null when the outcome says it all. */
  detail: string | null
  /** Fresh derived state for attempted rows; null on refusals. */
  inviteState: InviteState | null
  inviteExpiresAt: string | null
}

/** Per-row results in request order (deduplicated), plus the counts the
 * result line reads out. NOT all-or-nothing — see the endpoint docs. */
export interface BulkResendInvitesResult {
  results: BulkResendRowResult[]
  queued: number
  skipped: number
}

/** Durable identity edit. Participation changes use enrollment operations. */
export interface UpdateStudentBody {
  firstName: string
  lastName: string
  username: string | null
  addressSuffix: 'san' | 'none'
  identityVersion: number
}

/** One explicit identity row of an enrollment batch. */
export interface EnrollmentBatchIdentityRow {
  clientEmployeeId?: string | null
  clientDepartment?: string | null
  email: string
  firstName: string
  lastName: string
  username?: string | null
  addressSuffix: 'san' | 'none'
}

export interface EnrollmentBatchPreviewBody {
  cohortId: string
  rows: CreateEnrollmentBatchRow[]
}

export interface CreateEnrollmentBatchBody extends EnrollmentBatchPreviewBody {
  previewToken: string
}

export interface CreateEnrollmentBatchRow extends EnrollmentBatchIdentityRow {
  placementRequirement: PlacementRequirement
}

export interface CreateEnrollmentBatchRowResult {
  userId: string
  enrollmentId: string
  email: string
  identityCreated: boolean
  notificationState: 'queued' | 'existing_activation'
}

export interface CreateEnrollmentBatchResponse {
  clientId: string
  cohortId: string
  created: number
  rows: CreateEnrollmentBatchRowResult[]
}

export type EnrollmentBatchPreflightClassification =
  | 'new_student'
  | 'returning_student'
  | 'conflict'

export interface EnrollmentBatchPreflightRow {
  /** Zero-based index into the submitted review list. */
  index: number
  email: string
  classification: EnrollmentBatchPreflightClassification
  /** Present only for same-client students; never leaks a foreign identity. */
  userId: string | null
  studentName: string | null
  reusablePlacementLevel: CurriculumCefrLevel | null
  conflictCode: 'identity_unavailable' | 'period_overlap' | 'client_company_information_conflict' | null
  conflictingEnrollmentId: string | null
  conflictingEnrollmentStatus: EnrollmentStatus | null
  conflictingStartsOn: string | null
  conflictingEndsOn: string | null
}

export interface EnrollmentBatchPreflightResponse {
  previewToken: string
  cohort: Pick<CohortRow,
    | 'name' | 'scheduledStartOn' | 'accessEndsOn' | 'inviteSendAt' | 'inviteSendOffsetDays'
    | 'defaultTier' | 'defaultProgramId' | 'defaultTrophyGoal' | 'defaultPlacementRequirement'
    | 'defaultCurriculumProgressRequirement' | 'defaultTrophyProgressRequirement'
    | 'externalPolicyRef' | 'isTrial'
  >
  clientEmployeeIdEnabled: boolean
  clientDepartmentEnabled: boolean
  clientId: string
  cohortId: string
  newStudents: number
  returningStudents: number
  conflicts: number
  rows: EnrollmentBatchPreflightRow[]
}

/**
 * The extra keys the batch routes put beside `{code, message}` in an
 * object-shaped `detail` (AdminApiError.payload). `rows` are 0-based
 * indexes into the request's row list.
 */
export interface BatchRowErrorPayload {
  code?: string
  message?: string
  rows?: number[]
  emails?: string[]
}

/** Answer from the create-form's email pre-check. A bare boolean by design. */
export interface EmailAvailability {
  /** Normalized (trimmed, lowercased) — echo it back so a stale reply can be
   *  matched against what the field currently holds. */
  email: string
  available: boolean
}

export type EnrollmentStatus =
  | 'draft'
  | 'pending'
  | 'active'
  | 'suspended'
  | 'ended'
  | 'cancelled'

export type PlacementRequirement =
  | 'require_new_diagnostic'
  | 'reuse_latest_result'
export type CurriculumProgressRequirement =
  | 'start_new_attempt'
  | 'continue_latest_progress'
export type TrophyProgressRequirement =
  | 'start_new_progress'
  | 'continue_latest_progress'

export interface EnrollmentTermsBody {
  cohortId: string | null
  scheduledStartOn: string | null
  accessEndsOn: string | null
  tier: ProductTier | null
  programId: string | null
  trophyGoal: number | null
  placementRequirement: PlacementRequirement
  curriculumProgressRequirement: CurriculumProgressRequirement
  trophyProgressRequirement: TrophyProgressRequirement
  externalPolicyRef?: string | null
  continuitySourceEnrollmentId?: string | null
  continuityCreationKey?: string | null
}

/** Reuse the identity without resubmitting company-private preferences. */
export interface ExistingStudentEnrollmentBody extends EnrollmentTermsBody {
  userId: string
}

export interface CreateEnrollmentBody extends EnrollmentTermsBody {
  clientEmployeeId?: string | null
  clientDepartment?: string | null
  email: string
  firstName: string
  lastName: string
  username?: string | null
  addressSuffix: 'san' | 'none'
}

export interface EnrollmentContinuity {
  reservationId: string
  sourceEnrollmentId: string
  status: 'reserved' | 'finalized' | 'blocked' | 'cancelled' | 'expired'
  blockedReason: ContinuityBlockedReason | null
  blockedAt: string | null
  automaticAttemptCount: number
  consecutiveAutomaticFailureCount: number
  lastAutomaticAttemptAt: string | null
  nextAutomaticAttemptAt: string | null
  lastAutomaticErrorCode: 'continuity_rule_conflict' | 'unexpected_error' | null
}

export type ContinuityBlockedReason =
  | 'source_enrollment_not_ended'
  | 'source_call_active'
  | 'source_post_call_fact_pending'
  | 'source_post_call_job_pending'
  | 'automatic_finalization_failed'

export interface EnrollmentProgressPreview {
  placementLevel: string | null
  placementCompletedAt: string | null
  placementSource: 'diagnostic' | 'reused' | null
  practicedTopicCount: number
  earnedTrophyCount: number
  goalEvidenceStatus: 'not_reached' | 'reached' | 'revoked'
  goalReachedAt: string | null
  goalEvidenceVerificationId: string | null
  goalEvidenceTrophyCount: number | null
  certificates: Array<{
    certificateId: string
    locale: string
    issuedAt: string
    storageUri: string
  }>
}

export interface EnrollmentRow {
  enrollmentId: string
  userId: string
  cohortId: string | null
  cohortName: string | null
  scheduledStartOn: string | null
  accessEndsOn: string | null
  tier: ProductTier | null
  programId: string | null
  initialProgramLevelId: string | null
  program: ProgramSummary | null
  initialProgramLevel: ProgramLevelSummary | null
  chosenCefrLevel?: CurriculumCefrLevel | null
  trophyGoal: number | null
  goalSource: 'hr_confirmed_out_of_band' | 'cooori_confirmed_private' | null
  hrVisibilityScope: 'cohort_visible_to_hr' | 'private' | null
  placementRequirement: PlacementRequirement | null
  curriculumProgressRequirement: CurriculumProgressRequirement | null
  trophyProgressRequirement: TrophyProgressRequirement | null
  externalPolicyRef: string | null
  /** Analytics-only term snapshotted when this enrollment was locked. */
  isTrial: boolean
  termsLockedAt: string | null
  status: EnrollmentStatus
  participationStartedAt: string | null
  endedOn: string | null
  endedAt: string | null
  endReason: string | null
  continuity: EnrollmentContinuity | null
  progressPreview: EnrollmentProgressPreview | null
}

export interface EnrollmentListResponse {
  enrollments: EnrollmentRow[]
}

export interface EnrollmentMutationResponse {
  enrollment: EnrollmentRow
  identityCreated: boolean
  notificationState: 'queued' | 'existing_activation'
  notificationDeliveryId: string | null
  exactJobEnqueued: boolean
}

export interface ReplaceEnrollmentBody {
  reason: string
  replacement: EnrollmentTermsBody
}

export interface ContinuityRetryResponse {
  enrollment: EnrollmentRow
  outcome: 'finalized' | 'deferred' | 'expired'
  deferredReason: ContinuityBlockedReason | null
}
